/**
 * IndexedDB durability mirror for the localStorage save keys.
 * localStorage stays the synchronous source of truth (the whole game reads it
 * at boot); every write is mirrored to IndexedDB fire-and-forget, and at boot
 * any key that is missing or unparseable in localStorage is restored from the
 * mirror before the app constructs. This survives localStorage eviction
 * (notably iOS Safari's 7-day purge exempts IndexedDB under "installed" use)
 * and gives a second recovery layer beyond the .bak key (FEATURE_MATRIX §8).
 */

const DB_NAME = 'shape-shifters-save';
const DB_VERSION = 1;
const STORE = 'kv';

/** Save keys worth mirroring (raw localStorage strings). */
export const MIRRORED_KEYS = [
  'shape-shifters.profile.v1',
  'shape-shifters.progress.v1',
  'shape-shifters.settings.v1',
] as const;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  return (dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  }));
}

function idbAvailable(): boolean {
  return typeof indexedDB !== 'undefined' && typeof localStorage !== 'undefined';
}

/** Mirror one key's current localStorage value to IndexedDB (fire-and-forget). */
export function mirrorKey(key: string): void {
  if (!idbAvailable()) return;
  const raw = localStorage.getItem(key);
  if (raw === null) return;
  openDb()
    .then(
      (db) =>
        new Promise<void>((resolve, reject) => {
          const tx = db.transaction(STORE, 'readwrite');
          tx.objectStore(STORE).put(raw, key);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error ?? new Error('IndexedDB write failed'));
        }),
    )
    .catch((e: unknown) => console.warn('Save mirror write failed.', e));
}

/** Restore mirrored keys into localStorage where the local copy is missing or
 * unparseable. Resolves when done; call once at boot before reading saves. */
export async function restoreFromMirror(): Promise<void> {
  if (!idbAvailable()) return;
  try {
    const db = await openDb();
    for (const key of MIRRORED_KEYS) {
      const local = localStorage.getItem(key);
      if (local !== null) {
        try {
          JSON.parse(local);
          continue; // healthy local copy wins
        } catch {
          /* corrupt — fall through to the mirror */
        }
      }
      const mirrored = await new Promise<string | null>((resolve, reject) => {
        const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
        req.onsuccess = () => resolve(typeof req.result === 'string' ? req.result : null);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB read failed'));
      });
      if (mirrored === null) continue;
      try {
        JSON.parse(mirrored); // never restore junk
        localStorage.setItem(key, mirrored);
        console.warn(`Save key "${key}" restored from the IndexedDB mirror.`);
      } catch {
        /* mirror itself corrupt — leave local state alone */
      }
    }
  } catch (e) {
    console.warn('Save mirror restore skipped.', e);
  }
}

/** Mirror every save key now (call after meaningful writes, e.g. profile save). */
export function mirrorAll(): void {
  for (const key of MIRRORED_KEYS) mirrorKey(key);
}
